/**
 * <disc-set-form>: choosing a Moonphase disc set. One element, two homes:
 *
 *   - the Disc Set product page (snippets/disc-set-chooser.liquid, rendered
 *     by sections/disc-set-chooser.liquid and by sections/product.liquid for
 *     the disc-set product): no data-material, so the element renders and
 *     manages the metal grid ([data-metals], the consultation's option cells)
 *     as well as the counts, names what the chosen count fits ([data-fits])
 *     and preselects from `?material=ZR` and `?discs=8`;
 *   - a block for one metal (data-material="ZR"): the count cells only, the
 *     price line reading "<n> × <metal> · <price>".
 *
 * Reads the Moonphase catalog and the disc-set product's variants from the JSON
 * scripts inside it, looks the SKU up per metal and count, disables what is not
 * offered and gates PLACE IN BAG on the Ordering enabled setting and a
 * purchasable variant. The metal grid holds the offered metals only: a metal
 * the pricing model has withdrawn (`available: false`, still priced and in the
 * library) gets no cell, and `?material=` and the default choose from the same
 * offered list.
 *
 * A metal whose every count has sold out keeps its cell, with the sold-out
 * label as its note; a count whose variant has sold out stays enabled. With a
 * sold-out SKU chosen the section's sold-out block ([data-sold-out], from
 * snippets/sold-out.liquid) replaces the button and its notes, its link
 * carrying the SKU as the composition code.
 */

import { addLines, formatMoney } from "@theme/cart";
import { SOLD_OUT, buildSkuIndex, findSku, fitColumns, setSoldOutCode } from "@theme/composition";

const NOT_OFFERED = "Not offered";
const DEFAULT_COUNT = 3;

/** "A5 Side Bound and A4 Top Bound" */
function listAnd(items) {
  const words = items.map(String);
  return words.length > 1 ? `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}` : (words[0] ?? "");
}

function span(className, text = "") {
  const node = document.createElement("span");
  node.className = className;
  node.textContent = text;
  return node;
}

class DiscSetForm extends HTMLElement {
  #catalog = null;
  #variants = {};
  #skuIndex = {};
  #material = "";
  #discs = null;
  #busy = false;
  #metalCells = new Map();
  #resizeObserver = null;

  connectedCallback() {
    this.#catalog = this.#readJson("script#moonphase-catalog");
    this.#variants = this.#readJson("script[data-variants-for]") ?? {};
    if (!this.#catalog?.skus) return;

    this.#skuIndex = buildSkuIndex(this.#catalog.skus);
    this.#buildCells();

    const params = new URLSearchParams(window.location.search);
    const fixed = this.dataset.material ?? "";
    if (fixed) {
      this.#material = fixed;
      this.#discs = this.#pickCount(null);
      // A metal block with nothing to offer removes itself.
      if (this.#discs === null) {
        this.hidden = true;
        return;
      }
    } else {
      this.#buildMetalCells();
      this.#material = this.#pickMetal(params.get("material"));
      this.#discs = this.#pickCount(params.get("discs"));
      this.#resizeObserver = new ResizeObserver(() => this.#fitGrid());
      this.#resizeObserver.observe(this);
    }

    this.addEventListener("click", this.#onClick);
    this.#render();
    this.#fitGrid();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#onClick);
    this.#resizeObserver?.disconnect();
  }

  #readJson(selector) {
    const script = this.querySelector(selector);
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  /* Cells. */

  /** One cell per disc-set size in the catalog, in catalog order; availability is applied in #render. */
  #buildCells() {
    const container = this.querySelector("[data-counts]");
    if (!container) return;
    const sizes = (this.#catalog.discSets ?? []).map((set) => Number(set.discs)).filter((discs) => discs > 0);
    container.style.gridTemplateColumns = `repeat(${sizes.length}, minmax(0, 1fr))`;
    container.replaceChildren(
      ...sizes.map((discs) => {
        const cell = document.createElement("button");
        cell.type = "button";
        cell.className = "dmw-segmented__cell";
        cell.dataset.discs = String(discs);
        cell.setAttribute("aria-pressed", "false");
        cell.textContent = String(discs);
        return cell;
      }),
    );
  }

  /** One option cell per offered metal, in rank order: tag (SELECTED when pressed), name and blurb. */
  #buildMetalCells() {
    const grid = this.querySelector("[data-metals]");
    if (!grid) return;
    this.#metalCells = new Map();
    grid.replaceChildren(
      ...this.#metalOptions().map((option) => {
        const cell = document.createElement("button");
        cell.type = "button";
        cell.className = "dmw-option";
        cell.dataset.metal = option.code;
        cell.disabled = !option.enabled;
        cell.setAttribute("aria-pressed", "false");
        cell.append(
          span("dmw-option__tag"),
          span("dmw-option__name", option.name),
          span("dmw-option__note", option.enabled ? option.note : NOT_OFFERED),
        );
        this.#metalCells.set(option.code, cell);
        return cell;
      }),
    );
  }

  #fitGrid() {
    const grid = this.querySelector("[data-metals]");
    if (grid) grid.style.setProperty("--cols", fitColumns(grid.clientWidth, grid.children.length));
  }

  #counts() {
    return [...this.querySelectorAll("[data-discs]")].map((cell) => Number(cell.dataset.discs));
  }

  /* Offers. */

  /** The disc-set SKU for a count and metal, priced from the variant map when the product exists, else from the catalog. */
  #entryFor(discs, material = this.#material) {
    const sku = findSku(this.#skuIndex, { type: "discset", discs, material });
    if (!sku) return null;

    if (Object.keys(this.#variants).length > 0) {
      const variant = this.#variants[sku];
      if (!variant) return { sku, variant: null, price: null, available: false, soldOut: false, currency: undefined };
      return {
        sku,
        variant,
        price: variant.price,
        available: Boolean(variant.available),
        soldOut: Boolean(variant.soldOut),
        currency: undefined,
      };
    }

    const record = this.#catalog.skus[sku];
    return {
      sku,
      variant: null,
      price: Math.round(record.price * 100),
      available: Boolean(record.available),
      soldOut: false,
      currency: this.#catalog.meta?.currency ?? "USD",
    };
  }

  /** Available, or sold out: the customer may still choose it. */
  #offered(entry) {
    return Boolean(entry?.available || entry?.soldOut);
  }

  /** The offered metals only (a withdrawn one gets no cell); one is sold out only when every count of it is, not offered when the store has no set of it at all. */
  #metalOptions() {
    const counts = this.#counts();
    return (this.#catalog.materials ?? [])
      .filter((material) => material.available !== false)
      .map((material) => {
        const entries = counts.map((discs) => this.#entryFor(discs, material.code));
        const available = entries.some((entry) => entry?.available);
        return {
          code: material.code,
          name: material.name,
          note: available ? material.blurb : this.#soldOutLabel,
          enabled: entries.some((entry) => this.#offered(entry)),
        };
      });
  }

  #materialName() {
    const material = (this.#catalog.materials ?? []).find((entry) => entry.code === this.#material);
    return material?.name ?? this.dataset.materialName ?? "";
  }

  /** The metal to start on, from the offered list: the one asked for when it has a set to choose, else the first that has. */
  #pickMetal(wanted) {
    const options = this.#metalOptions();
    const enabled = options.filter((option) => option.enabled).map((option) => option.code);
    const asked = (wanted ?? "").toUpperCase();
    if (enabled.includes(asked)) return asked;
    return enabled[0] ?? options[0]?.code ?? "";
  }

  /** The count to start on: the one asked for when it is offered, else 3 when it can be bought, else the first that can be, else the first offered; null when none. */
  #pickCount(wanted) {
    const entries = this.#counts().map((discs) => [discs, this.#entryFor(discs)]);
    const offered = entries.filter(([, entry]) => this.#offered(entry)).map(([discs]) => discs);
    const available = entries.filter(([, entry]) => entry?.available).map(([discs]) => discs);
    const asked = Number(wanted);
    if (offered.includes(asked)) return asked;
    if (available.includes(DEFAULT_COUNT)) return DEFAULT_COUNT;
    return available[0] ?? offered[0] ?? null;
  }

  get #orderingEnabled() {
    return document.documentElement.dataset.orderingEnabled === "true";
  }

  get #soldOutLabel() {
    return this.dataset.soldOutLabel || SOLD_OUT;
  }

  #canAdd(entry) {
    return this.#orderingEnabled && Boolean(entry?.variant) && entry.available;
  }

  /* Rendering. */

  #render() {
    for (const [code, cell] of this.#metalCells) {
      const pressed = code === this.#material;
      cell.setAttribute("aria-pressed", String(pressed));
      cell.firstElementChild.textContent = pressed ? "SELECTED" : "";
    }
    for (const cell of this.querySelectorAll("[data-discs]")) {
      const discs = Number(cell.dataset.discs);
      cell.disabled = !this.#offered(this.#entryFor(discs));
      cell.setAttribute("aria-pressed", String(discs === this.#discs));
    }

    const fits = this.querySelector("[data-fits]");
    if (fits) {
      const set = (this.#catalog.discSets ?? []).find((entry) => Number(entry.discs) === this.#discs);
      fits.textContent = set?.fits?.length ? `Fits ${listAnd(set.fits)}` : "";
    }

    const entry = this.#entryFor(this.#discs);
    const soldOut = Boolean(entry?.soldOut);
    const price = this.querySelector("[data-price]");
    if (price) {
      const amount = entry && entry.price !== null ? formatMoney(entry.price, entry.currency) : "";
      if (!this.dataset.material) price.textContent = this.#offered(entry) ? amount : "—";
      else if (soldOut) price.textContent = `${this.#discs} × ${this.#materialName()} · ${this.#soldOutLabel}`;
      else price.textContent = amount ? `${this.#discs} × ${this.#materialName()} · ${amount}` : "";
    }

    // Sold out replaces the button and its notes with the sold-out block, whatever the ordering gate says.
    const canAdd = this.#canAdd(entry) && !this.#busy;
    const button = this.querySelector("[data-add]");
    const note = this.querySelector("[data-gate-note]");
    const dispatch = this.querySelector("[data-dispatch-note]");
    const block = this.querySelector("[data-sold-out]");
    if (button) {
      button.hidden = soldOut;
      if (canAdd) button.removeAttribute("aria-disabled");
      else button.setAttribute("aria-disabled", "true");
    }
    if (note) note.hidden = soldOut || this.#canAdd(entry);
    if (dispatch) dispatch.hidden = soldOut;
    if (block) {
      block.hidden = !soldOut;
      if (soldOut) setSoldOutCode(block, this.dataset.soldOutUrl, entry.sku);
    }
  }

  #showError(message) {
    const error = this.querySelector("[data-error]");
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const metal = event.target.closest("[data-metal]");
    if (metal && this.contains(metal)) {
      if (metal.disabled || metal.dataset.metal === this.#material) return;
      this.#material = metal.dataset.metal;
      // The count stays when the new metal offers it, else the usual first choice.
      this.#discs = this.#pickCount(this.#discs);
      this.#showError("");
      this.#render();
      return;
    }

    const cell = event.target.closest("[data-discs]");
    if (cell && this.contains(cell)) {
      if (cell.disabled) return;
      this.#discs = Number(cell.dataset.discs);
      this.#showError("");
      this.#render();
      return;
    }

    const button = event.target.closest("[data-add]");
    if (button && this.contains(button)) {
      event.preventDefault();
      this.#add(button);
    }
  };

  async #add(button) {
    const entry = this.#entryFor(this.#discs);
    if (this.#busy || !this.#canAdd(entry)) return;

    this.#busy = true;
    this.#showError("");
    this.#render();
    try {
      await addLines([{ id: entry.variant.id, quantity: 1 }]);
      document.querySelector("bag-drawer")?.open(button);
    } catch (error) {
      this.#showError(error instanceof Error ? error.message : "The discs could not be placed in your bag.");
    } finally {
      this.#busy = false;
      this.#render();
    }
  }
}

if (!customElements.get("disc-set-form")) {
  customElements.define("disc-set-form", DiscSetForm);
}
